import { ensureFreshSession, refreshOnce } from "@auth/refreshMiddleware";
import { useSessionStore } from "@auth/sessionStore";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";

import { getConfig } from "../config";

const WS_URL = `${getConfig().wsUrl}/ws/notifications`;
const BEARER_SUBPROTOCOL = "scribenote.auth.bearer.v1";

const BASE_BACKOFF_MS = 1_000;
const MAX_BACKOFF_MS = 30_000;
const CLOSE_AUTH_REQUIRED = 1008;

type EntityType =
  | "task"
  | "artifact"
  | "event"
  | "client"
  | "patient"
  | "room"
  | "subject"
  | "visit"
  | "visit_stage"
  | "user"
  | "view";

interface NotificationFrame {
  /** `op` is "updated" for entity updates (tasks only today); absent on creates. */
  events?: {
    type?: string;
    uuid?: string;
    label?: string;
    op?: string;
    kind?: string;
  }[];
}

// Both subject roots: `["subject-search", q]` for the search results and
// `["subject", id]` for the open pane's header. React Query matches key
// elements exactly, so one prefix cannot stand in for the other.
const SUBJECT_PREFIXES = ["subject-search", "subject"];

// Membership here is also the frame allowlist, so an entity type stays listed
// even when nothing reads it yet — `event` earns its keep through the
// capture-node path below rather than through any prefix.
const PREFIXES_BY_TYPE: Record<EntityType, readonly string[]> = {
  // A pims_write card's lifecycle arrives as task frames (staged → checked
  // off), and the activity view's staff bucket + settled history mirror it —
  // `usePimsActivity` has no polling, so this is its only cross-device refresh.
  task: ["tasks", "task", "notifications", "pimsActivity"],
  artifact: ["artifacts"],
  event: [],
  client: SUBJECT_PREFIXES,
  patient: SUBJECT_PREFIXES,
  room: SUBJECT_PREFIXES,
  subject: SUBJECT_PREFIXES,
  // A new visit or a stage transition refreshes the patient-flow rail (today
  // list) and the expanded flow tracker (visit detail) — both under "visits".
  visit: ["visits"],
  visit_stage: ["visits"],
  user: SUBJECT_PREFIXES,
  // A view frame is a create, a re-render, or a surfacing change (BE-1909):
  // refresh the lists (incl. the dashboard slot's surfaced set) and any open
  // view detail. The subject summary is a plain view (WEB-3256), so a write
  // that changes it arrives here — that is the whole summary-refresh story.
  view: ["views", "view"],
};

// Capture-node connect/disconnect arrives as a generic `event` frame whose
// `label` is the event_type (`capture_node.connected` / `.disconnected`) —
// the room it's about is the event's primary subject, not in the frame. We key
// off the label to refetch the capture roster and per-room dots.
const isCaptureNodeEvent = (e: { type: string; label?: string }): boolean =>
  e.type === "event" && !!e.label?.startsWith("capture_node.");

export const useNotificationsSocket = () => {
  const queryClient = useQueryClient();
  const userUuid = useSessionStore((s) => s.user?.userUuid ?? null);

  useEffect(() => {
    if (!userUuid) return;

    let ws: WebSocket | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let attempt = 0;
    let disposed = false;

    const handleFrame = (raw: string) => {
      let frame: NotificationFrame;
      try {
        frame = JSON.parse(raw);
      } catch {
        return;
      }
      const events = (frame.events ?? []).filter(
        (
          e,
        ): e is {
          type: EntityType;
          uuid?: string;
          label?: string;
          op?: string;
          kind?: string;
        } =>
          !!e.type &&
          Object.prototype.hasOwnProperty.call(PREFIXES_BY_TYPE, e.type),
      );

      const prefixes = new Set<string>();
      for (const event of events) {
        for (const key of PREFIXES_BY_TYPE[event.type]) prefixes.add(key);
        // capture_node.* rides the generic `event` type, so its prefixes don't
        // reach the capture roster — refresh it explicitly off the label.
        if (isCaptureNodeEvent(event)) prefixes.add("capture");
      }
      for (const prefix of prefixes) {
        void queryClient.invalidateQueries({ queryKey: [prefix] });
      }
    };

    function scheduleReconnect() {
      if (disposed || reconnectTimer) return;
      const delay = Math.min(BASE_BACKOFF_MS * 2 ** attempt, MAX_BACKOFF_MS);
      attempt += 1;
      reconnectTimer = setTimeout(() => {
        reconnectTimer = null;
        void connect();
      }, delay);
    }

    async function connect() {
      if (disposed) return;
      const session = await ensureFreshSession();
      if (disposed) return;
      if (!session || session.scope !== "user") {
        scheduleReconnect();
        return;
      }

      const socket = new WebSocket(WS_URL, [
        BEARER_SUBPROTOCOL,
        session.accessToken,
      ]);
      ws = socket;

      socket.onopen = () => {
        attempt = 0;
      };

      socket.onmessage = (e) => {
        if (typeof e.data === "string") handleFrame(e.data);
      };

      socket.onerror = () => {
        socket.close();
      };

      socket.onclose = (e) => {
        if (ws === socket) ws = null;
        if (disposed) return;
        if (e.code === CLOSE_AUTH_REQUIRED) {
          void refreshOnce().then((rotated) => {
            if (disposed || !rotated) return;
            scheduleReconnect();
          });
          return;
        }
        scheduleReconnect();
      };
    }

    void connect();

    return () => {
      disposed = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (ws) {
        ws.onopen = ws.onmessage = ws.onerror = ws.onclose = null;
        ws.close();
      }
    };
  }, [userUuid, queryClient]);
};
